import { useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import { ButtonIcon, Flex, Text } from '@backstage/ui';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: 'var(--bui-space-2)',
    // Scope Backstage's internal-slot overrides to this notification toast.
    '[class*="Toast_content__"]:has(&)': {
      flex: 1,
      minWidth: 0,
    },
    '[class*="Toast_surface__"]:has(&) > [class*="Toast_closeButton__"]': {
      display: 'none',
    },
  },
  actions: {
    width: '100%',
    justifyContent: 'flex-end',
    flexShrink: 0,
    color: 'var(--bui-fg-primary)',
  },
  error: {
    margin: 0,
  },
});

type NotificationToastActionsProps = {
  notificationId: string;
  onDismiss: () => void;
};

export function NotificationToastActions({
  notificationId,
  onDismiss,
}: NotificationToastActionsProps) {
  const classes = useStyles();
  const notificationsApi = useApi(notificationsApiRef);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const markAsRead = async () => {
    setPending(true);
    setError(false);
    try {
      await notificationsApi.updateNotifications({
        ids: [notificationId],
        read: true,
      });
      onDismiss();
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={classes.root}>
      <Flex gap="1" className={classes.actions}>
        <ButtonIcon
          aria-label="Mark as read"
          size="small"
          variant="tertiary"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          isDisabled={pending}
          isPending={pending}
          onPress={() => void markAsRead()}
        />
        <ButtonIcon
          aria-label="Open notifications"
          size="small"
          variant="tertiary"
          icon={<OpenInNewIcon fontSize="inherit" />}
          onPress={() => {
            window.open('/notifications', '_blank');
            onDismiss();
          }}
        />
      </Flex>
      {error && (
        <Text as="p" role="alert" color="danger" className={classes.error}>
          Failed to mark notification as read. Please try again.
        </Text>
      )}
    </div>
  );
}
